import * as React from 'react';
import {observer} from 'mobx-react';
import {classes, style} from 'typestyle';
import {colorContrast, colorSlate, colorSubtle, colorWhite} from '../theme/color';
import {zAppMenuDrawer} from '../theme/z';
import {center, centerCenter, endJustified, horizontal, start, startJustified, vertical} from 'csstips';
import {deps, inject, StatefulComponent} from '../lib/component';
import {mediaDesktop} from '../theme/media';
import {constantDrawerWidth, constantHeaderHeight, constantNavLinkHeight} from '../theme/constant';
import UserStatusBar from './user/status-bar';
import {SVGIcon, SVGIconTypes} from './svg-icon';
import {resetAnchorStyles} from '../theme/reset';
import {em, important, percent, px, rem} from 'csx';
import {ButtonPill} from './button-pill';
import {formatCurrency} from '../lib/currency';
import {ReleaseTrack} from './release-track';
import {AddRemoveCompat} from './compat/add-remove-compat';
import {PlayButtonCompat} from './compat/play-button-compat';
import {FavouriteButtonCompat} from './compat/favourite-button-compat';
import {BrandSponsoredLabelCompat} from './compat/brand-sponsored-label-compat';
import {AccountRateCard} from './account/rate-card';
import {gradientDrawer} from '../theme/gradient';
import {Alert} from './alert';
import {UIBrowseSection} from '../types/ui';
import {AccountMenu} from './account/menu';

export type Props = {
  i18n?: {
    signOut: string;
  }
}

export type State = {
  accountExpanded: boolean;
}

export type NavLink = {
  href: string;
  label: string;
  icon?: keyof SVGIconTypes
}

@inject(deps) @observer
export default class AppMenuDrawer extends StatefulComponent<Props, State> {
  state = {
    accountExpanded: false
  };

  render() {
    const {accountExpanded} = this.state;
    const {i18n = AppMenuDrawer.i18n, model} = this.props;
    const {loading, loadingChannels, channel, channels} = this.props.model.user;
    const {user, mustReAuth} = this.props.model.auth;
    const {showMenuDrawer} = this.props.model.ui;
    const {showChannelAddModal, showChannelSelectModal, hideMustReAuthModal} = this.props.controller.modal;
    const {browseSections} = model.content;

    const className = classes(
      AppMenuDrawer.styles.container,
      showMenuDrawer && AppMenuDrawer.styles.containerOpen,
    );

    const lowerLinks = AppMenuDrawer.defaultLowerLinks;

    const userLinks = accountExpanded
      ? AppMenuDrawer.defaultAccountLinks
      : AppMenuDrawer.defaultAccountLinks.slice(0, 1);

    return (
      <div id="app-menu-drawer" className={className}>
        <div className={classes(AppMenuDrawer.styles.headerToggle, AppMenuDrawer.styles.hideDesktop)} onClick={this.hideDrawer}>
          <SVGIcon.Cross size={22} />
        </div>
        <div className={classes(AppMenuDrawer.styles.fixed, AppMenuDrawer.styles.hideDesktop)}>
          <UserStatusBar className={classes(AppMenuDrawer.styles.statusBar, user && AppMenuDrawer.styles.statusBarActive)} />
        </div>
        <div className={AppMenuDrawer.styles.main}>
          {!user && mustReAuth && (
            <Alert className={AppMenuDrawer.styles.reAuth} type="warning" position="top-right" onClose={hideMustReAuthModal}>
              You've been signed out
            </Alert>
          )}
          {user && (
            <h3 className={AppMenuDrawer.styles.heading}>My YouTube channel</h3>
          )}
          {user && (
            <AccountRateCard
              className={AppMenuDrawer.styles.rateCard}
              user={user}
              channel={channel}
              loading={loading || loadingChannels}
            />
          )}
          {user && (
            <div className={AppMenuDrawer.styles.basketActions}>
              <ButtonPill muted={true} onClick={channels.length > 1 ? showChannelSelectModal : showChannelAddModal}>
                {channels.length > 1 ? 'Change channel' : 'Add another channel'}
              </ButtonPill>
            </div>
          )}
          <h3 className={AppMenuDrawer.styles.heading}>Find music</h3>
          <div className={AppMenuDrawer.styles.nav}>
            {AppMenuDrawer.defaultUpperLinks.map(this.renderNavLink)}
            {!!browseSections && !!browseSections.length && browseSections.map(AppMenuDrawer.mapToNavLink).map(this.renderNavLink)}
            {!!user && AppMenuDrawer.signedInUpperLinks.map(this.renderNavLink)}
          </div>
          <div className={AppMenuDrawer.styles.basket} />
          <div className={AppMenuDrawer.styles.fixed}>
            <h3 className={AppMenuDrawer.styles.heading}>More info</h3>
            <div className={AppMenuDrawer.styles.nav}>
              {lowerLinks.map(this.renderNavLink)}
              {user && userLinks.map(this.renderUserLink)}
            </div>
          </div>
        </div>
        {user && (
          <div className={AppMenuDrawer.styles.fixed}>
            <div className={AppMenuDrawer.styles.basketActions}>
              <ButtonPill muted={true} className={AppMenuDrawer.styles.basketAction} onClick={this.logout}>
                Sign out
              </ButtonPill>
            </div>
          </div>
        )}
      </div>
    )
  }

  logout = () => {
    this.props.controller.auth.logout();
  };

  renderNavLink = (navLink: NavLink, index: number) => {
    const {handleClickLink} = this.props.controller.ui;

    const Icon = navLink.icon && SVGIcon[navLink.icon];

    return (
      <a key={index} className={AppMenuDrawer.styles.navLink} onClick={handleClickLink} href={navLink.href}>
        <div className={AppMenuDrawer.styles.navIcon}>
          {Icon && <Icon size={20} />}
        </div>
        <div>{navLink.label}</div>
      </a>
    )
  };

  static mapToNavLink = ({slug, label}: UIBrowseSection): NavLink => ({
    href: `/browse/${slug}`,
    label,
  });

  renderUserLink = (navLink: NavLink, index: number) => {
    const {handleClickLink} = this.props.controller.ui;
    const {accountExpanded} = this.state;

    const Icon = navLink.icon && SVGIcon[navLink.icon];

    const onClick = index === 0
      ? this.toggleAccount
      : handleClickLink;

    return (
      <a key={index} className={AppMenuDrawer.styles.navLink} onClick={onClick} href={navLink.href}>
        <div className={AppMenuDrawer.styles.navIcon}>
          {Icon && <Icon size={20} />}
        </div>
        <div>{navLink.label}</div>
        {index === 0 && (
          <SVGIcon.ArrowRight className={classes(AppMenuDrawer.styles.arrow, accountExpanded && AppMenuDrawer.styles.arrowExpanded)} color={colorWhite.toString()} size={10} />
        )}
      </a>
    )
  };

  toggleAccount = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    this.setState(state => ({accountExpanded: !state.accountExpanded}));
  }

  hideDrawer = () => {
    this.props.controller.ui.setMenuDrawer(false)
  };

  static i18n = {
    signOut: 'Sign out'
  };

  static styles = {
    container: style(
      {
        ...vertical,
        zIndex: zAppMenuDrawer,
        background: colorSlate.darken(0.075).fade(0.9).toString(),
        color: colorWhite.toString(),
        position: 'fixed',
        top: 0,
        right: 0,
        bottom: 0,
        width: constantDrawerWidth,
        transform: `translate3d(${constantDrawerWidth},0,0)`
      },
      mediaDesktop({
        top: constantHeaderHeight
      })
    ),
    containerOpen: style({
      transform: `translate3d(0,0,0)`
    }),
    fixed: style({
      ...vertical,
      ...start,
      ...startJustified,
      position: 'relative',
      flexShrink: 0,
    }),
    heading: style({
      padding: '0 1rem',
      margin: '2rem 0 1rem',
      fontSize: rem(1.25),
      flexShrink: 0,
      $nest: {
        '&:first-child': {
          margin: '1rem 0'
        }
      }
    }),
    statusBar: style(
      {
        width: percent(100),
        height: constantHeaderHeight,
        padding: `0 ${constantHeaderHeight} 0 0`,
      },
      mediaDesktop({
        background: important(colorSlate.toString())
      })
    ),
    statusBarActive: style({
      background: gradientDrawer
    }),
    headerToggle: style(
      {
        ...centerCenter,
        zIndex: 1,
        position: 'absolute',
        top: 0,
        right: 0,
        width: constantHeaderHeight,
        height: constantHeaderHeight,
        cursor: 'pointer'
      },
    ),
    hideDesktop: style(
      mediaDesktop({display: 'none'})
    ),
    main: style({
      ...vertical,
      flex: 1,
      padding: '0 0 1rem 0',
      overflowY: 'auto'
    }),
    nav: style({
      ...vertical,
      width: percent(100),
      flexShrink: 0,
    }),
    navLink: style({
      ...horizontal,
      ...center,
      ...startJustified,
      position: 'relative',
      color: colorWhite.toString(),
      fontWeight: 500,
      padding: '0 16px 0 0',
      $nest: {
        ...resetAnchorStyles(colorWhite.toString())
      }
    }),
    navIcon: style({
      ...centerCenter,
      width: constantHeaderHeight,
      height: constantNavLinkHeight,
    }),
    basket: style({
      flex: '1 0 auto',
      padding: '0 1rem',
      minHeight: rem(3),
    }),
    basketEmpty: style({
      ...horizontal,
      ...center,
      color: colorSubtle.toString(),
      fontSize: rem(0.75),
      $nest: {
        '& svg': {
          margin: '0 0.25rem'
        }
      }
    }),
    basketActions: style({
      ...horizontal,
      ...endJustified,
      ...center,
      height: constantHeaderHeight,
      width: percent(100),
      padding: '0 1rem',
      flexShrink: 0,
    }),
    originalPrice: style({
      textDecoration: 'line-through',
      marginRight: '0.25rem'
    }),
    basketAction: style({
      maxWidth: percent(100),
      $nest: {
        '& svg': {
          margin: '0 0.25rem'
        },
        '& svg:last-child': {
          margin: '0 0 0 0.25rem'
        }
      }
    }),
    rateCard: style({
      width: percent(100),
      padding: '1rem'
    }),
    coupon: style({
      margin: '1rem 0 0 0',
      border: `2px dashed ${colorContrast.fade(0.2).toString()}`,
      padding: '1rem',
      $nest: {
        '& div': {
          color: colorContrast.fade(0.75).toString(),
          margin: '0 0 0.5rem 0',
          fontSize: '1rem',
          fontWeight: 500,
          lineHeight: '1rem',
        },
        '& p': {
          color: colorWhite.fade(0.75).toString(),
          margin: 0,
          fontSize: rem(0.875),
          lineHeight: em(1.4),
        }
      }
    }),
    reAuth: style({
      flexShrink: 0,
      margin: important('1rem 1rem 0'),
      padding: important('0.5rem 1rem')
    }),
    arrow: style({
      position: important('absolute'),
      top: percent(50),
      marginTop: px(-5),
      right: px(16),
      transform: 'rotate(90deg)',
      stroke: colorWhite.toString(),
    }),
    arrowExpanded: style({
      transform: important('rotate(270deg)'),
    })
  }

  static defaultUpperLinks: NavLink[] = [
    {
      href: '/search',
      label: 'Search',
      icon: 'Search'
    },
    {
      href: '/browse',
      label: 'Browse',
      icon: 'Browse'
    },
  ];

  static signedInUpperLinks: NavLink[] = [
    {
      href: '/account/favourites',
      label: 'Favourites',
      icon: 'Favourite'
    }
  ];

  static defaultLowerLinks: NavLink[] = [
    // {
    //   href: '/articles',
    //   label: 'Articles',
    //   icon: 'Book'
    // },
    {
      href: '/about',
      label: 'About',
      icon: 'About'
    },
    {
      href: '/faq',
      label: 'FAQ',
      icon: 'FAQ'
    },
    {
      href: '/pricing',
      label: 'Pricing',
      icon: 'Pricing'
    },
  ];

  static defaultAccountLinks: NavLink[] = [
    { href: '/account', label: 'My account', icon: 'SignIn' },
    ...AccountMenu.data.links.map(l => ({
      href: l.to,
      label: l.label
    }))
  ]
}